import type { Point, Wall } from '../types'; // Assuming types are in ../types
import { doLinesIntersect } from './floorPlanUtils'; // Assuming doLinesIntersect is here

// Helper function: orientation
// Using a small tolerance for floating point comparisons
const orientation = (p: Point, q: Point, r: Point): number => {
  const tolerance = 1e-7; // Small tolerance value
  const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
  
  if (Math.abs(val) < tolerance) return 0; // Collinear (with tolerance)
  return (val > 0) ? 1 : 2; // Clockwise or Counterclockwise
};

// Helper: is point p on the segment ab (excluding endpoints a and b)?
const isPointOnSegmentInterior = (p: Point, a: Point, b: Point, tolerance = 1e-7): boolean => {
    const distAP = Math.hypot(p.x - a.x, p.y - a.y);
    const distBP = Math.hypot(p.x - b.x, p.y - b.y);
    const distAB = Math.hypot(a.x - b.x, a.y - b.y);
    // Check collinearity
    if (Math.abs(distAP + distBP - distAB) > tolerance) {
        return false; 
    }
    // Check if p is strictly between a and b (not an endpoint)
    return distAP > tolerance && distBP > tolerance;
};

export const checkRectangleWallIntersectionUniqueName = ( // Renamed function
    newRoomStart: Point, 
    newRoomEnd: Point, 
    currentWalls: Wall[]
): boolean => {
    const topLeft = { x: Math.min(newRoomStart.x, newRoomEnd.x), y: Math.min(newRoomStart.y, newRoomEnd.y) };
    const topRight = { x: Math.max(newRoomStart.x, newRoomEnd.x), y: Math.min(newRoomStart.y, newRoomEnd.y) };
    const bottomLeft = { x: Math.min(newRoomStart.x, newRoomEnd.x), y: Math.max(newRoomStart.y, newRoomEnd.y) };
    const bottomRight = { x: Math.max(newRoomStart.x, newRoomEnd.x), y: Math.max(newRoomStart.y, newRoomEnd.y) };
    
    const newRoomEdges: Array<{name: string, start: Point, end: Point}> = [
      { name: "top", start: topLeft, end: topRight },
      { name: "right", start: topRight, end: bottomRight },
      { name: "bottom", start: bottomRight, end: bottomLeft },
      { name: "left", start: bottomLeft, end: topLeft }
    ];

  

    for (let i = 0; i < currentWalls.length; i++) {
      const existingWall = currentWalls[i];
      for (let j = 0; j < newRoomEdges.length; j++) {
        const newEdge = newRoomEdges[j];
        
        if (typeof doLinesIntersect !== 'function') {
  
          return true; 
        }
        
        if (doLinesIntersect(newEdge.start, newEdge.end, existingWall.start, existingWall.end)) {
          

          const isStrictlyCollinearAndOverlapping = 
            orientation(newEdge.start, newEdge.end, existingWall.start) === 0 &&
            orientation(newEdge.start, newEdge.end, existingWall.end) === 0 &&
            orientation(existingWall.start, existingWall.end, newEdge.start) === 0 && 
            orientation(existingWall.start, existingWall.end, newEdge.end) === 0;

          if (isStrictlyCollinearAndOverlapping) {
            const newMinX = Math.min(newEdge.start.x, newEdge.end.x);
            const newMaxX = Math.max(newEdge.start.x, newEdge.end.x);
            const newMinY = Math.min(newEdge.start.y, newEdge.end.y);
            const newMaxY = Math.max(newEdge.start.y, newEdge.end.y);

            const existingMinX = Math.min(existingWall.start.x, existingWall.end.x);
            const existingMaxX = Math.max(existingWall.start.x, existingWall.end.x);
            const existingMinY = Math.min(existingWall.start.y, existingWall.end.y);
            const existingMaxY = Math.max(existingWall.start.y, existingWall.end.y);
            
            const xOverlap = Math.max(0, Math.min(newMaxX, existingMaxX) - Math.max(newMinX, existingMinX));
            const yOverlap = Math.max(0, Math.min(newMaxY, existingMaxY) - Math.max(newMinY, existingMinY));

            const newLength = Math.hypot(newEdge.end.x - newEdge.start.x, newEdge.end.y - newEdge.start.y);
            const existingLength = Math.hypot(existingWall.end.x - existingWall.start.x, existingWall.end.y - existingWall.start.y);

            if ((newLength > 1e-7 && xOverlap > 1e-7) || (newLength > 1e-7 && yOverlap > 1e-7) || (existingLength > 1e-7 && xOverlap > 1e-7) || (existingLength > 1e-7 && yOverlap > 1e-7)  ) {
                const newStartOnExisting = isPointOnSegmentInterior(newEdge.start, existingWall.start, existingWall.end, 1e-7) || 
                                           (Math.abs(newEdge.start.x - existingWall.start.x) < 1e-7 && Math.abs(newEdge.start.y - existingWall.start.y) < 1e-7) ||
                                           (Math.abs(newEdge.start.x - existingWall.end.x) < 1e-7 && Math.abs(newEdge.start.y - existingWall.end.y) < 1e-7);
                const newEndOnExisting = isPointOnSegmentInterior(newEdge.end, existingWall.start, existingWall.end, 1e-7) ||
                                         (Math.abs(newEdge.end.x - existingWall.start.x) < 1e-7 && Math.abs(newEdge.end.y - existingWall.start.y) < 1e-7) ||
                                         (Math.abs(newEdge.end.x - existingWall.end.x) < 1e-7 && Math.abs(newEdge.end.y - existingWall.end.y) < 1e-7);
                
                const existingStartOnNew = isPointOnSegmentInterior(existingWall.start, newEdge.start, newEdge.end, 1e-7) ||
                                           (Math.abs(existingWall.start.x - newEdge.start.x) < 1e-7 && Math.abs(existingWall.start.y - newEdge.start.y) < 1e-7) ||
                                           (Math.abs(existingWall.start.x - newEdge.end.x) < 1e-7 && Math.abs(existingWall.start.y - newEdge.end.y) < 1e-7);
                const existingEndOnNew = isPointOnSegmentInterior(existingWall.end, newEdge.start, newEdge.end, 1e-7) ||
                                         (Math.abs(existingWall.end.x - newEdge.start.x) < 1e-7 && Math.abs(existingWall.end.y - newEdge.start.y) < 1e-7) ||
                                         (Math.abs(existingWall.end.x - newEdge.end.x) < 1e-7 && Math.abs(existingWall.end.y - newEdge.end.y) < 1e-7);

                if ((newStartOnExisting && newEndOnExisting) || (existingStartOnNew && existingEndOnNew)) {
        
                    continue;
                }
            }
          }

          let isLCorner = false;
          const newEndpoints = [newEdge.start, newEdge.end];
          const existingEndpoints = [existingWall.start, existingWall.end];
          let sharedEndpoint: Point | null = null;

          for (const nep of newEndpoints) {
              for (const eep of existingEndpoints) {
                  if (Math.abs(nep.x - eep.x) < 0.1 && Math.abs(nep.y - eep.y) < 0.1) {
                      isLCorner = true;
                      sharedEndpoint = nep; 
                      break;
                  }
              }
              if (isLCorner) break;
          }

          if (isLCorner && sharedEndpoint) {

              continue; 
          }

          const vNew = { x: newEdge.end.x - newEdge.start.x, y: newEdge.end.y - newEdge.start.y };
          const vExisting = { x: existingWall.end.x - existingWall.start.x, y: existingWall.end.y - existingWall.start.y };
          const dotProduct = vNew.x * vExisting.x + vNew.y * vExisting.y;
          const segmentsArePerpendicular = Math.abs(dotProduct) < 1e-7; 

          let isAllowedTJunction = false;

          if (isPointOnSegmentInterior(newEdge.start, existingWall.start, existingWall.end) ||
              isPointOnSegmentInterior(newEdge.end, existingWall.start, existingWall.end)) {
              if (segmentsArePerpendicular) {
    
                  isAllowedTJunction = true;
              } else {
  
              }
          }

          if (!isAllowedTJunction && 
              (isPointOnSegmentInterior(existingWall.start, newEdge.start, newEdge.end) ||
               isPointOnSegmentInterior(existingWall.end, newEdge.start, newEdge.end))) {
              if (segmentsArePerpendicular) {
    
                  isAllowedTJunction = true;
              } else {
  
              }
          }

          if (isAllowedTJunction) {
              continue; 
          }
          
          
          return true; 
        }
      }
    }
  
    return false;
  };
